import { IMAGE_TIERS } from '@/theme/tokens';

/**
 * Single place that knows where artwork lives (§51).
 *
 * Images are synced from `_WEB/` by `npm run assets:sync` and served from
 * `/assets/...`. Swapping a placeholder for final artwork is a file copy, never
 * a component change.
 */

export const ASSET_BASE = '/assets';

export const asset = (relativePath: string): string =>
  `${ASSET_BASE}/${relativePath.replace(/^\/+/, '')}`;

export type CardTier = keyof typeof IMAGE_TIERS;

/** `cards/{deck}/w{size}/{nn}.webp` — the one path template all cards follow. */
export const cardImage = (deckSlug: string, index: number, tier: CardTier = 'cardPlay'): string =>
  asset(`cards/${deckSlug}/${IMAGE_TIERS[tier]}/${String(index).padStart(2, '0')}.webp`);

export const cardBackImage = (deckSlug: string, tier: CardTier = 'cardPlay'): string =>
  asset(`cards/${deckSlug}/${IMAGE_TIERS[tier]}/back.webp`);

/**
 * Rewrites a stored w420 image path to another tier. Card paths in the database
 * are stored at the play tier; grids want thumbnails and previews want full size.
 */
export function atTier(storedPath: string, tier: CardTier): string {
  const target = IMAGE_TIERS[tier];
  return storedPath.replace(/\/w\d+\//, `/${target}/`);
}

export const BRAND = {
  sunMark: asset('brand/sun-gold.svg'),
  sunLogo: asset('brand/w512/sun-logo.webp'),
} as const;

export const TABLE_TEXTURE = asset('table/w1232/table-bg.webp');

/**
 * The bare glyph, for counters and labels. At 18 px the two-tone disc of the
 * physical chip turns to mud, so the interface uses the glyph on its own.
 *
 * Keyed by `ResourceKey` plus `talent`, which is a card rather than a counter.
 * The bag's artwork is filed under `money` — the printed token's name — while
 * the state calls it `money_bag` to keep it apart from the numeric balance.
 */
export const RESOURCE_TOKEN_ICONS = {
  presence: asset('tokens/cell/presence.svg'),
  support: asset('tokens/cell/support.svg'),
  money_bag: asset('tokens/cell/money.svg'),
  talent: asset('tokens/cell/talent.svg'),
} as const;

/**
 * The chip itself — glyph on its sand disc, as it looks on the table. Used
 * where a resource is an object lying on the board, not a number in a panel.
 */
export const RESOURCE_CHIP_ICONS = {
  presence: asset('tokens/resource/presence.svg'),
  support: asset('tokens/resource/support.svg'),
  money_bag: asset('tokens/resource/money.svg'),
  talent: asset('tokens/resource/talent.svg'),
} as const;

/**
 * The 100 000 banknote, for the numeric balance (GAME-33).
 *
 * Дарья asked for the bill next to the balance so it cannot be mistaken for a
 * bag: «А изображение купюры-то можно поставить?». It is a wide 2:1 image, not
 * a disc, which is itself the visual cue that this counter is a different kind
 * of thing from the three resources.
 */
export const MONEY_NOTE_IMAGE = asset('money/w200/banknote-100000.webp');
